import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/portal";
  const prefillEmail = new URLSearchParams(location.search).get("email") || "";
  const [form, setForm] = useState({ email: prefillEmail, password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(form.email, form.password);
      navigate(user.role === "admin" ? "/admin" : from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your ATF WebWorks account.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        {error && <div className="bg-destructive/15 border border-destructive/40 text-red-300 text-sm px-4 py-3" data-testid="login-error">{error}</div>}
        <Field label="Email">
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="login-email" className={inputCls} placeholder="you@company.com" />
        </Field>
        <Field label="Password">
          <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="login-password" className={inputCls} placeholder="••••••••" />
        </Field>
        <div className="text-right -mt-2">
          <Link to="/forgot-password" className="text-sm text-gray-400 hover:text-gold transition-colors" data-testid="forgot-link">Forgot password?</Link>
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full bg-gold text-navy py-4 font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {loading && <Loader2 className="animate-spin" size={18} />} Sign In
        </button>
      </form>
      <Divider />
      <button onClick={googleLogin} data-testid="google-login-btn"
        className="w-full border border-white/20 text-white py-4 font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-3">
        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
        Continue with Google
      </button>
      <p className="text-center text-gray-400 text-sm mt-6">
        New here? <Link to="/register" className="text-gold hover:underline" data-testid="go-register">Create an account</Link>
      </p>
    </AuthShell>
  );
}

const inputCls = "w-full bg-navy border border-white/20 text-white p-3 focus:border-gold focus:outline-none";
export const Field = ({ label, children }) => (
  <div><label className="text-sm text-gray-400 mb-2 block">{label}</label>{children}</div>
);
export const Divider = () => (
  <div className="flex items-center gap-4 my-6"><div className="h-px bg-white/10 flex-1" /><span className="text-xs text-gray-500 uppercase tracking-widest">or</span><div className="h-px bg-white/10 flex-1" /></div>
);

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6 py-24 grain">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-10">
          <div className="w-11 h-11 border border-gold flex items-center justify-center font-heading font-bold text-gold text-lg">ATF</div>
          <span className="font-heading text-2xl text-white font-semibold">WebWorks</span>
        </Link>
        <div className="glass p-8 md:p-10">
          <h1 className="font-heading text-3xl text-white mb-2">{title}</h1>
          <p className="text-gray-400 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
